import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, IconButton } from '@material-ui/core';
import { Close, InfoOutlined, OpenInNewOutlined } from '@material-ui/icons';
import { useLocation } from 'react-router';
import moment from 'moment';

import { setInspectingMediaData } from '../../../actions/inspectScreenActions';
import { mediaNames, mediaTypes } from '../../../constants/mediaConstants';
import { screenRoutes } from '../../../constants/routesPath';
import useFetchMediaDetails from '../InspectScreen/useFetchMedia';
import { portalsUrl } from '../../../utils/config';
import InteractMediaLibraryModal from '../../../reusable/MediaLibrary/InteractMediaLibraryModal';
import ConfirmationDialog from '../../../reusable/ConfirmationDialog/ConfirmationDialog';
import { getDifferenceBetweenTime } from '../../../services/timeStampService';

import MediaContent from './inspectMediaMiniContent/MediaContent';
import { MediaDetailUtils } from './utils';
import PortalContent from './inspectMediaMiniContent/PortalContent';
import FormContent from './inspectMediaMiniContent/FormContent';

import './InspectMedia.scss';
import { getAccountSettings } from '../../../actions/profileActions';
import { refreshMedias } from '../../../actions/publishActions';

interface InspectMediaMiniProps {}
const baseClassName = 'inspect-media-mini';

const InspectMediaMini = ({}: InspectMediaMiniProps) => {
	const dispatch = useDispatch<any>();
	const { t } = useTranslation();
	const location = useLocation();
	const { defaultAccountId } = useSelector((state: any) => state.session);
	const { inspectingMediaData } = useSelector((state: any) => state.inspectScreenReducer);
	const isPortal = inspectingMediaData?.type === mediaTypes.portal;
	const isForm = inspectingMediaData?.type === mediaTypes.form;

	const actionContainerHTMLRef = useRef<HTMLDivElement | null>(null);
	const playerContainerHTMLRef = useRef<HTMLDivElement | null>(null);
	const headerHTMLRef = useRef<HTMLDivElement | null>(null);
	const contentContainerHTMLRef = useRef<HTMLDivElement | null>(null);
	const miniMediaLibraryRef = useRef<any>();

	const mediaDetailRef = useRef<any>();

	const [refreshData, setRefreshData] = useState<boolean>(true);
	const [refreshMediasData, setRefreshMediasData] = useState<boolean>(true);
	const [isMediaModified, setIsMediaModified] = useState<boolean>(false);
	const [isThumbnailChanged, setIsThumbnailChanged] = useState<boolean>(false);
	const [selectedTab, setSelectedTab] = useState<string>(MediaDetailUtils.MenuItems[0].value);
	const [selectedPlayerConfig, setSelectedPlayerConfig] = useState<string>('');
	const [showWarningDialog, setShowWarningDialog] = useState<boolean>(false);
	const [durationFromLastUpdated, setDurationFromLastUpdated] = useState('');

	//portalState
	const [enablePublishPortal, setEnablePublishPortal] = useState<boolean>(false);
	const [portalPreviewUrl, setPortalPreviewUrl] = useState<string>('');
	const [accountPreset, setAccountPreset] = useState<string[]>([]);

	const fetchAccountSetting = useCallback(() => {
		dispatch(getAccountSettings(defaultAccountId)).then((settings: any) => {
			if (settings?.qbrickPlayer?.[0]?.presets?.defaults) {
				setAccountPreset(settings?.qbrickPlayer?.[0]?.presets?.defaults ?? []);
			}
		});
	}, [defaultAccountId, dispatch]);

	const { mediaDetails, publishPortalDetails } = useFetchMediaDetails({
		mediaId: inspectingMediaData?.id,
		mediaName: mediaNames.medias,
		mediaType: inspectingMediaData?.type,
		refreshData,
		resetRefreshData: () => setRefreshData(false),
		errorMessage: t('COMMON_MEDIA_NOT_FOUND_OR_NO_PERMISSION'),
	});

	const isPortalUnPublished = useMemo(() => {
		const published = publishPortalDetails?.content;
		const unPublished = mediaDetails?.content;

		return (
			publishPortalDetails?.themeId !== mediaDetails?.themeId ||
			published?.name !== unPublished?.name ||
			published?.shareThumbnail !== unPublished?.shareThumbnail ||
			published?.protectedMedias !== unPublished?.protectedMedias ||
			JSON.stringify(published?.main?.banner) !== JSON.stringify(unPublished?.main?.banner) ||
			JSON.stringify(published?.main?.heroVideo) !== JSON.stringify(unPublished?.main?.heroVideo) ||
			JSON.stringify(published?.main?.playlists) !== JSON.stringify(unPublished?.main?.playlists) ||
			JSON.stringify(published?.main?.searchFilters) !== JSON.stringify(unPublished?.main?.searchFilters) ||
			JSON.stringify(published?.footer) !== JSON.stringify(unPublished?.footer)
		);
	}, [mediaDetails, publishPortalDetails]);

	const handleOnDataChanges = () => {
		setIsMediaModified(true);
	};

	const onPlayerConfigChange = (selectedConfig: any) => {
		setSelectedPlayerConfig(selectedConfig?.id);
		killPlayer();
		setTimeout(initPlayer);
	};

	const handleSaveChanges = (_event: React.MouseEvent) => {
		mediaDetailRef.current?.onSaveChanges(() => setIsMediaModified(false));
	};

	const initPlayer = () => {
		if (
			(window as any).QplayerLoader.initiateInteractivePlayer &&
			typeof (window as any).QplayerLoader.initiateInteractivePlayer === 'function'
		) {
			(window as any).QplayerLoader.initiateInteractivePlayer();
		}
	};

	const killPlayer = () => {
		const playerRef = document.querySelector('#mini-media-detail-player-preview');
		if (playerRef) {
			playerRef.innerHTML = '';
		}
	};

	const handleCloseMiniMediaInspectView = useCallback(() => {
		killPlayer();
		setIsMediaModified(false);
		setSelectedTab(MediaDetailUtils.MenuItems[0].value);
		dispatch(setInspectingMediaData({}));
	}, [dispatch]);

	const handleConfirmationSubmitted = useCallback(
		({ ok }: any) => {
			ok && handleCloseMiniMediaInspectView();
			setShowWarningDialog(false);
		},
		[handleCloseMiniMediaInspectView]
	);

	const handleCloseMiniDetailClick = useCallback(() => {
		if (isMediaModified) {
			setShowWarningDialog(true);
			return;
		}
		handleCloseMiniMediaInspectView();
	}, [handleCloseMiniMediaInspectView, isMediaModified]);

	const handleExpandButtonClick = useCallback(() => {
		if (!mediaDetails?.id) {
			return;
		}
		const fullPath = `${window.location.origin}#${screenRoutes.PUBLISH_MEDIA_LIBRARY}/medias/${mediaDetails?.id}/expand`;
		window.open(fullPath, '_blank');
	}, [mediaDetails?.id]);

	const handleResizeScreen = useCallback(() => {
		document.body.style.overflow = 'hidden';
		const containerHeight = window.innerHeight;

		const contentHeight =
			containerHeight -
			8 -
			(headerHTMLRef.current?.getBoundingClientRect().height ?? 0) -
			(playerContainerHTMLRef.current?.getBoundingClientRect().height ?? 0) -
			(actionContainerHTMLRef.current?.getBoundingClientRect().height ?? 0);

		if (contentContainerHTMLRef?.current) {
			contentContainerHTMLRef.current.style.height = contentHeight + 'px';
		}
	}, []);

	useEffect(() => {
		if (refreshMediasData) {
			dispatch(refreshMedias());
			setRefreshMediasData(false);
		}
	}, [refreshMediasData]);

	useEffect(() => {
		fetchAccountSetting();
	}, [defaultAccountId]);

	useEffect(() => {
		setRefreshData(true);
		killPlayer();
		const bodyOverFlow = inspectingMediaData?.id ? 'hidden' : '';
		document.body.style.overflow = bodyOverFlow;
	}, [inspectingMediaData]);

	useEffect(() => {
		// skip initiating the player when there are no mediaDetails or when the media type is form
		const isForm = inspectingMediaData?.type === mediaTypes.form;
		if (!mediaDetails?.id || !inspectingMediaData?.id || isForm) {
			return;
		}
		if (
			(window as any).QplayerLoader.initiateInteractivePlayer &&
			typeof (window as any).QplayerLoader.initiateInteractivePlayer === 'function'
		) {
			(window as any).QplayerLoader.initiateInteractivePlayer();
			handleResizeScreen();
		}

		return () => killPlayer();
	}, [handleResizeScreen, mediaDetails, inspectingMediaData]);

	useEffect(() => {
		handleResizeScreen();
		window.addEventListener('resize', handleResizeScreen);
		return () => window.removeEventListener('resize', handleResizeScreen);
	}, [handleResizeScreen]);

	useEffect(() => {
		handleCloseMiniMediaInspectView();
	}, [location]);

	const checkSavingStatus = () => {
		const lastUpdated =
			mediaDetails?.updated || mediaDetails?.modified
				? new Date(mediaDetails.updated ?? mediaDetails.modified)
				: null;
		if (!lastUpdated) {
			setDurationFromLastUpdated('');
			return;
		}
		const now = new Date();
		const duration =
			moment(new Date()).diff(lastUpdated) > 1000 ? getDifferenceBetweenTime(now, lastUpdated, t) : t('COMMON_A_MOMENT_AGO');
		setDurationFromLastUpdated(duration);
	};

	useEffect(() => {
		checkSavingStatus();
		const calculatedDurationFromLastUpdatedTimer = setInterval(checkSavingStatus, 10000);
		return () => {
			clearInterval(calculatedDurationFromLastUpdatedTimer);
		};
	}, [mediaDetails]);

	const renderHeader = () => {
		const expandIcon = (
			<IconButton onClick={handleExpandButtonClick}>
				<OpenInNewOutlined htmlColor="#000000" />
			</IconButton>
		);
		const closeIcon = (
			<IconButton onClick={handleCloseMiniDetailClick} style={{ marginRight: '-12px' }}>
				<Close htmlColor="#000000" />
			</IconButton>
		);

		if (isPortal) {
			return (
				<>
					<div style={{ flex: '1' }}>{t('INSPECT_MEDIA_MINI_TITLE')}</div>
					{expandIcon}
					{closeIcon}
				</>
			)
		} else if (isForm) {
			return (
				<>
					<div style={{ flex: '1', padding: '18px 0' }}>{mediaDetails.name}</div>
					{closeIcon}
				</>
			)
		} else {
			return (
				<>
					<div style={{ flex: '1' }}>{t('INSPECT_MEDIA_MINI_TITLE')}</div>
					{closeIcon}
				</>
			)
		}
	};

	return mediaDetails?.id ? (
		<>
			<div className={`${baseClassName}__container`}>
				<div className={`${baseClassName}__header`} ref={(r) => (headerHTMLRef.current = r)}>
					{renderHeader()}
				</div>

				{isPortal &&
					<PortalContent
						baseClassName={baseClassName}
						portalDetail={mediaDetails}
						handleOnDataChanges={handleOnDataChanges}
						setIsThumbnailChanged={setIsThumbnailChanged}
						ref={mediaDetailRef}
						miniMediaLibraryRef={miniMediaLibraryRef}
						accountPreset={accountPreset}
						reset={() => {
							setEnablePublishPortal(false);
							setIsMediaModified(false);
							const previewUrl = `${portalsUrl}${mediaDetails.code}`;
							setPortalPreviewUrl(previewUrl);
						}}
					/>
				}
				{/*
					This code and all of its related logic in this file is preserved for now,
					in case we decide to show the forms in the InspectMediaMini again at some point in time.
					Currently all of this is handled inside FormsAndRegistrationScreen (more local approach).
				*/}
				{isForm &&
					<FormContent
						baseClassName={baseClassName}
						formDetail={mediaDetails}
						formMediaId={inspectingMediaData.formMediaId}
						handleOnDataChanges={handleOnDataChanges}
						handleOnDataChangesCallback={() => setIsMediaModified(false)}
						ref={mediaDetailRef}
					/>
				}

				{!isPortal && !isForm &&
					<>
						<div
							className={`${baseClassName}__player-container`}
							ref={(r) => (playerContainerHTMLRef.current = r)}
						>
							<div style={{ position: 'relative', paddingBottom: '56.25%' }}>
								<div
									id={'mini-media-detail-player-preview'}
									style={{ position: 'absolute', top: '0', left: '0' }}
									title={mediaDetails?.title}
									data-qplayer-account-id={defaultAccountId}
									data-qplayer-media-id={mediaDetails?.id}
									data-qplayer-preset-id={selectedPlayerConfig}
								></div>
							</div>
						</div>
						<MediaContent
							baseClassName={baseClassName}
							mediaDetails={mediaDetails}
							contentContainerHTMLRef={contentContainerHTMLRef}
							mediaDetailRef={mediaDetailRef}
							selectedTab={selectedTab}
							setSelectedTab={setSelectedTab}
							handleOnDataChanges={handleOnDataChanges}
							onPlayerConfigChange={onPlayerConfigChange}
						/>
					</>
				}

				<div className={`${baseClassName}__actions`} ref={(r) => (actionContainerHTMLRef.current = r)}>
					{durationFromLastUpdated && (
						<span className={`${baseClassName}__saving-status`}>
							{`${t('SCENARIO_STORYBOARD_LAST_SAVED')} ${durationFromLastUpdated}`}
						</span>
					)}
					<div>
						{isPortal &&
							<>
								<Button
									color="primary"
									onClick={() => {
										window.open(portalPreviewUrl, '_blank');
									}}
									variant="outlined"
									style={{ marginRight: 10 }}
								>
									{t('Preview')}
								</Button>
								<Button
									variant="contained"
									color="primary"
									disabled={!isMediaModified}
									onClick={() => {
										mediaDetailRef.current?.onSaveChanges((previewUrl: string) => {
											setEnablePublishPortal(true);
											setIsMediaModified(false);
											setPortalPreviewUrl(previewUrl);
											setRefreshData(true);
											if (isThumbnailChanged) {
												setRefreshMediasData(true);
											}
										});
									}}
									style={{ marginRight: 10 }}
								>
									{t('Save as draft')}
								</Button>
								<Button
									variant="contained"
									color="primary"
									disabled={!(enablePublishPortal || isPortalUnPublished)}
									onClick={() => {
										mediaDetailRef.current?.onPublishPortal((previewUrl: string) => {
											setEnablePublishPortal(false);
											setIsMediaModified(false);
											setPortalPreviewUrl(previewUrl);
											setRefreshData(true);
										});
									}}
								>
									{t('COMMON_PUBLISH')}
								</Button>
							</>
						}
						{isForm &&
							<Button
								variant="contained"
								color="primary"
								disabled={!isMediaModified}
								onClick={handleSaveChanges}
								style={{ marginTop: 14 }}
							>
								{t('COMMON_SAVE_CHANGES')}
							</Button>
						}
						{!isPortal && !isForm &&
							<Button
								variant="contained"
								color="primary"
								disabled={!isMediaModified}
								onClick={handleSaveChanges}
							>
								{t('COMMON_SAVE_CHANGES')}
							</Button>
						}
					</div>
				</div>
			</div>

			{isPortal && (
				<InteractMediaLibraryModal
					mediaType={'video'}
					hiddenStockLibrary
					hiddenInteractLibrary={false}
					ref={miniMediaLibraryRef}
					handleReturnedMedia={(media) => {
						mediaDetailRef?.current?.onSelectedHeroMedia(media);
					}}
				/>
			)}

			{showWarningDialog && (
				<ConfirmationDialog
					title=" "
					icon={<InfoOutlined htmlColor="#126cfc" />}
					callback={handleConfirmationSubmitted}
					message={t('INSPECT_MEDIA_MINI_CONFIRM_MSG')}
					okButtonText={t('COMMON_YES')}
					cancelButtonText={t('COMMON_CANCEL')}
				/>
			)}
		</>
	) : null;
};

export default InspectMediaMini;
